/**
 * Analytics Service
 * Handles GTM (Google Tag Manager) tracking with cross-browser compatibility
 * Focuses on maintaining Facebook Pixel and other marketing platform integrations
 */

import GTM_CONFIG, { isDevelopment, isGTMLoaded, isDataLayerReady } from '../config/analytics.config';

class AnalyticsService {
  constructor() {
    this.initialized = false;
    this.gtmLoaded = false;
    this.eventQueue = [];
    this.retryCount = 0;
    this.maxRetries = 3;
    this.gtmContainerId = GTM_CONFIG.gtmContainerId;
    this.monitoringInterval = null;
  }

  /**
   * Initialize GTM tracking with cross-browser compatibility
   */
  async initialize() {
    if (this.initialized) return;

    // Set up dataLayer
    window.dataLayer = window.dataLayer || [];

    // Check GTM status
    this.checkGTMStatus();

    // Set up GTM monitoring and retry logic
    this.setupGTMMonitoring();

    // Process queued events
    this.processEventQueue();

    this.initialized = true;

    if (isDevelopment) {
      console.log('GTM Analytics Service initialized');
    }
  }

  /**
   * Check if GTM is loaded and working
   */
  checkGTMStatus() {
    // Use helper function from config
    this.gtmLoaded = isGTMLoaded();
    
    if (this.gtmLoaded) {
      if (isDevelopment) {
        console.log('GTM loaded successfully');
      }
      this.processEventQueue();
    } else {
      if (isDevelopment) {
        console.warn('GTM not loaded, will retry...');
      }
    }
  }

  /**
   * Set up GTM monitoring with retry logic for cross-browser compatibility
   */
  setupGTMMonitoring() {
    // Clear any existing monitoring
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
    }

    // Set up periodic GTM health checks
    this.monitoringInterval = setInterval(() => {
      const wasLoaded = this.gtmLoaded;
      this.gtmLoaded = isGTMLoaded();
      
      // If GTM just loaded, process queued events
      if (!wasLoaded && this.gtmLoaded) {
        console.log('GTM loaded on retry, processing queued events');
        this.processEventQueue();
      }
      
      // If GTM is still not loaded and we haven't exceeded retries
      if (!this.gtmLoaded && this.retryCount < this.maxRetries) {
        this.retryCount++;
        this.pushToDataLayer({
          'event': 'gtm_retry_attempt',
          'retry_count': this.retryCount,
          'browser': this.getBrowserName(),
          'timestamp': new Date().toISOString()
        });
        
        if (isDevelopment) {
          console.warn(`GTM retry attempt ${this.retryCount}/${this.maxRetries}`);
        }
      }
      
      // Stop monitoring if GTM is loaded or max retries reached
      if (this.gtmLoaded || this.retryCount >= this.maxRetries) {
        clearInterval(this.monitoringInterval);
        this.monitoringInterval = null;
      }
    }, 2000);
  }

  /**
   * Track page view through GTM
   */
  trackPageView(pagePath, pageTitle) {
    const eventData = {
      event: 'page_view',
      page_path: pagePath || window.location.pathname,
      page_title: pageTitle || document.title,
      page_location: window.location.href,
      timestamp: new Date().toISOString(),
      browser: this.getBrowserName(),
    };

    // Send via dataLayer (GTM)
    this.pushToDataLayer(eventData);

    this.logEvent('Page View', eventData);
  }

  /**
   * Track custom event through GTM
   */
  trackEvent(eventName, parameters = {}) {
    // Validate event name
    if (!eventName || typeof eventName !== 'string') {
      console.error('Invalid event name:', eventName);
      return;
    }

    // Get event configuration
    const eventConfig = GTM_CONFIG.events[eventName] || {};
    
    // Prepare event data
    const eventData = {
      event: eventName,
      event_category: eventConfig.category || 'Custom',
      event_timestamp: new Date().toISOString(),
      browser: this.getBrowserName(),
      gtm_loaded: this.gtmLoaded,
      ...parameters,
    };

    // Queue event if GTM not ready
    if (!this.isReady()) {
      this.eventQueue.push({ eventName, eventData });
      return;
    }

    // Send event
    this.sendEvent(eventName, eventData);
  }

  /**
   * Send event via GTM dataLayer
   */
  sendEvent(eventName, eventData) {
    // Send via dataLayer (GTM)
    this.pushToDataLayer(eventData);

    this.logEvent(eventName, eventData);
  }

  /**
   * Push data to dataLayer with validation
   */
  pushToDataLayer(data) {
    if (isDataLayerReady()) {
      window.dataLayer.push(data);
      
      if (isDevelopment) {
        console.log('Pushed to dataLayer:', data);
      }
    } else {
      console.warn('dataLayer not ready, queuing event');
      this.eventQueue.push({ eventName: data.event, eventData: data });
    }
  }

  /**
   * Process queued events
   */
  processEventQueue() {
    if (!this.isReady() || this.eventQueue.length === 0) return;

    if (isDevelopment) {
      console.log(`Processing ${this.eventQueue.length} queued GTM events`);
    }
    
    while (this.eventQueue.length > 0) {
      const { eventName, eventData } = this.eventQueue.shift();
      this.sendEvent(eventName, eventData);
    }
  }

  /**
   * Track form submission
   */
  trackFormSubmit(formName, formData = {}) {
    this.trackEvent('form_submit', {
      form_name: formName,
      ...formData,
    });
  }

  /**
   * Track error
   */
  trackError(errorType, errorMessage, additionalData = {}) {
    this.trackEvent('error', {
      error_type: errorType,
      error_message: errorMessage,
      page_location: window.location.href,
      ...additionalData,
    });
  }

  /**
   * Track visualiser events
   */
  trackVisualiserEvent(action, configuration = {}) {
    const eventMap = {
      start: 'visualiser_start',
      save: 'visualiser_save_option',
      quote: 'visualiser_get_quote',
    };

    const eventName = eventMap[action];
    if (eventName) {
      this.trackEvent(eventName, configuration);
    }
  }

  /**
   * Helper methods
   */
  isReady() {
    return this.initialized && this.gtmLoaded && isDataLayerReady();
  }

  getBrowserName() {
    const ua = navigator.userAgent;
    if (ua.match(/chrome|chromium|crios/i) && !ua.match(/edg/i)) return 'Chrome';
    if (ua.match(/firefox|fxios/i)) return 'Firefox';
    if (ua.match(/safari/i) && !ua.match(/chrome|chromium|crios/i)) return 'Safari';
    if (ua.match(/edg/i)) return 'Edge';
    return 'Other';
  }

  logEvent(eventName, eventData) {
    if (isDevelopment || GTM_CONFIG.debug.logEvents) {
      console.group(`📊 GTM Event: ${eventName}`);
      console.log('Data:', eventData);
      console.log('GTM Loaded:', this.gtmLoaded);
      console.log('DataLayer Ready:', isDataLayerReady());
      console.log('Browser:', this.getBrowserName());
      console.groupEnd();
    }
  }

  /**
   * Debug information
   */
  getDebugInfo() {
    return {
      initialized: this.initialized,
      gtmLoaded: this.gtmLoaded,
      gtmContainerId: this.gtmContainerId,
      retryCount: this.retryCount,
      maxRetries: this.maxRetries,
      queuedEvents: this.eventQueue.length,
      dataLayerSize: window.dataLayer ? window.dataLayer.length : 0,
      dataLayerReady: isDataLayerReady(),
      browser: this.getBrowserName(),
      cookiesEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      monitoringActive: this.monitoringInterval !== null,
    };
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

// Auto-initialize on load
if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      analyticsService.initialize();
    });
  } else {
    analyticsService.initialize();
  }
}

export default analyticsService;